import { Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import type { AvailabilitySlot } from '@/types/offering';

const DAYS = [
  { value: 'MON', label: 'Mon' },
  { value: 'TUE', label: 'Tue' },
  { value: 'WED', label: 'Wed' },
  { value: 'THU', label: 'Thu' },
  { value: 'FRI', label: 'Fri' },
  { value: 'SAT', label: 'Sat' },
  { value: 'SUN', label: 'Sun' },
];

const TIMES: { value: string; label: string }[] = [];
for (let h = 0; h < 24; h++) {
  const ampm = h < 12 ? 'AM' : 'PM';
  const displayH = h % 12 === 0 ? 12 : h % 12;
  TIMES.push({ value: `${String(h).padStart(2, '0')}:00`, label: `${displayH}:00 ${ampm}` });
  TIMES.push({ value: `${String(h).padStart(2, '0')}:30`, label: `${displayH}:30 ${ampm}` });
}

interface Props {
  slots: AvailabilitySlot[];
  onChange: (slots: AvailabilitySlot[]) => void;
  disabled?: boolean;
}

export function AvailabilitySlotsBuilder({ slots, onChange, disabled }: Props) {
  const addSlot = () => {
    onChange([...slots, { dayOfWeek: 'MON', timeFrom: '18:00', timeTo: '20:00' }]);
  };

  const removeSlot = (i: number) => {
    onChange(slots.filter((_, idx) => idx !== i));
  };

  const updateSlot = (i: number, field: keyof AvailabilitySlot, value: string) => {
    const updated = slots.map((s, idx) =>
      idx === i ? { ...s, [field]: value } : s,
    );
    onChange(updated);
  };

  return (
    <div className="space-y-3">
      <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        Availability Slots
      </Label>
      {slots.length === 0 && (
        <p className="text-sm text-muted-foreground italic">No slots added yet.</p>
      )}
      {slots.map((slot, i) => (
        <div key={i} className="flex items-center gap-2 flex-wrap">
          <select
            value={slot.dayOfWeek}
            onChange={e => updateSlot(i, 'dayOfWeek', e.target.value)}
            disabled={disabled}
            className="h-9 rounded-md border border-transparent bg-muted/60 px-2 text-sm transition-colors focus:bg-background focus:outline-none focus:ring-2 focus:ring-ring"
          >
            {DAYS.map(d => (
              <option key={d.value} value={d.value}>{d.label}</option>
            ))}
          </select>
          <select
            value={slot.timeFrom}
            onChange={e => updateSlot(i, 'timeFrom', e.target.value)}
            disabled={disabled}
            className="h-9 rounded-md border border-transparent bg-muted/60 px-2 text-sm transition-colors focus:bg-background focus:outline-none focus:ring-2 focus:ring-ring"
          >
            {TIMES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
          <span className="text-muted-foreground text-sm">to</span>
          <select
            value={slot.timeTo}
            onChange={e => updateSlot(i, 'timeTo', e.target.value)}
            disabled={disabled}
            className="h-9 rounded-md border border-transparent bg-muted/60 px-2 text-sm transition-colors focus:bg-background focus:outline-none focus:ring-2 focus:ring-ring"
          >
            {TIMES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
          {!disabled && (
            <button
              type="button"
              onClick={() => removeSlot(i)}
              className="p-1.5 rounded-lg text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors cursor-pointer"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      ))}
      {!disabled && (
        <Button type="button" variant="outline" size="sm" className="gap-1.5 cursor-pointer" onClick={addSlot}>
          <Plus className="w-3.5 h-3.5" /> Add Slot
        </Button>
      )}
    </div>
  );
}
